import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { UserPlus, Users, Edit, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = { name: '', email: '', password: '', permissions: [] };

// Owner-only: create helper (assistant) teacher accounts and choose what each
// one may do. A helper signs in on the normal teacher login page.
const TeacherHelpers = ({ apiUrl, token }) => {
  const [open, setOpen] = useState(false);
  const [helpers, setHelpers] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchHelpers = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/teacher/helpers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setHelpers(data.helpers || []);
        setPermissionOptions(data.permissions || []);
      }
    } catch { /* ignore */ }
  }, [apiUrl, token]);

  useEffect(() => { if (token) fetchHelpers(); }, [token, fetchHelpers]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const togglePermission = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const startEdit = (h) => {
    setEditingId(h.id);
    setForm({ name: h.name, email: h.email, password: '', permissions: h.permissions || [] });
  };

  const handleSave = async () => {
    if (!form.name.trim() || (!editingId && !form.email.trim())) {
      toast.error('يرجى إدخال الاسم والبريد الإلكتروني');
      return;
    }
    if (!editingId && form.password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setSaving(true);
    try {
      const body = editingId
        ? { name: form.name, permissions: form.permissions, ...(form.password ? { password: form.password } : {}) }
        : form;
      const r = await fetch(`${apiUrl}/api/teacher/helpers${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(editingId ? 'تم تحديث المعلم المساعد' : 'تم إنشاء حساب المعلم المساعد');
        resetForm();
        await fetchHelpers();
      } else {
        toast.error(data.detail || 'فشل الحفظ');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSaving(false);
  };

  const handleToggleActive = async (h) => {
    try {
      const r = await fetch(`${apiUrl}/api/teacher/helpers/${h.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active: !h.active }),
      });
      if (r.ok) {
        const updated = await r.json();
        setHelpers((prev) => prev.map((x) => (x.id === h.id ? updated : x)));
      }
    } catch { toast.error('حدث خطأ'); }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`حذف حساب المعلم المساعد «${h.name}»؟`)) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/helpers/${h.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        setHelpers((prev) => prev.filter((x) => x.id !== h.id));
        if (editingId === h.id) resetForm();
        toast.success('تم حذف المعلم المساعد');
      }
    } catch { toast.error('حدث خطأ'); }
  };

  const labelOf = (key) => permissionOptions.find((p) => p.key === key)?.label || key;

  return (
    <>
      <Card className="border-[#E8DFCF] bg-[#FFFFFF]/80 mb-8">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#EF9F27]/10">
                <Users className="w-5 h-5 text-[#EF9F27]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#412402]">المعلمون المساعدون</h3>
                <p className="text-sm text-[#412402]/60">
                  {helpers.length > 0
                    ? `لديك ${helpers.length} معلم مساعد — يعملون على حسابك بالصلاحيات التي تحددها`
                    : 'أنشئ حساباً لمعلم مساعد وحدّد الصلاحيات التي يستطيع استخدامها'}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setOpen(true)} data-testid="helpers-btn">
              <UserPlus className="w-4 h-4 ml-1" />
              إدارة المساعدين
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>المعلمون المساعدون</DialogTitle>
            <DialogDescription>
              يسجّل المعلم المساعد الدخول من صفحة دخول المعلمين بالبريد وكلمة المرور التي تحددها هنا.
            </DialogDescription>
          </DialogHeader>

          {helpers.length > 0 && (
            <div className="space-y-2">
              {helpers.map((h) => (
                <div key={h.id} className="border border-[#E8DFCF] rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#412402] truncate">{h.name}</p>
                      <p className="text-sm text-[#412402]/60 truncate" dir="ltr">{h.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch checked={h.active} onCheckedChange={() => handleToggleActive(h)} />
                      <Button variant="ghost" size="sm" onClick={() => startEdit(h)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(h)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {!h.active && <Badge className="bg-red-100 text-red-700">موقوف</Badge>}
                    {(h.permissions || []).length === 0
                      ? <Badge variant="outline">بدون صلاحيات</Badge>
                      : h.permissions.map((p) => <Badge key={p} variant="outline">{labelOf(p)}</Badge>)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-[#E8DFCF] pt-4 space-y-3">
            <h4 className="font-semibold text-[#412402]">
              {editingId ? 'تعديل المعلم المساعد' : 'إضافة معلم مساعد'}
            </h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>الاسم</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="helper-name-input"
                />
              </div>
              <div>
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  dir="ltr"
                  value={form.email}
                  disabled={Boolean(editingId)}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="helper-email-input"
                />
              </div>
            </div>
            <div>
              <Label>{editingId ? 'كلمة مرور جديدة (اتركها فارغة للإبقاء على الحالية)' : 'كلمة المرور'}</Label>
              <Input
                type="text"
                dir="ltr"
                autoComplete="off"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="helper-password-input"
              />
            </div>
            <div>
              <Label>الصلاحيات</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {permissionOptions.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm text-[#412402] cursor-pointer">
                    <Checkbox
                      checked={form.permissions.includes(p.key)}
                      onCheckedChange={() => togglePermission(p.key)}
                      data-testid={`helper-perm-${p.key}`}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            {editingId && (
              <Button variant="outline" onClick={resetForm}>إلغاء التعديل</Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
              data-testid="helper-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 ml-1 animate-spin" />}
              {editingId ? 'حفظ التعديلات' : 'إنشاء الحساب'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeacherHelpers;
